import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AddBusinessOutlined,
  ArrowBack as ArrowBackIcon,
  BusinessOutlined,
  DirectionsCarOutlined,
  EmailOutlined,
  MoreHorizOutlined,
  PeopleAltOutlined,
  PhoneInTalkOutlined,
} from '@mui/icons-material';
import { salesLeadService } from '../../services/salesLeadService';
import { salesActivityLogService } from '../../services/salesActivityLogService';
import { salesPortalService } from '../../services/salesPortalService';
import {
  SalesEntityAvatar,
  SalesPageHeader,
  SalesSectionCard,
  SALES,
  salesPrimaryButtonSx,
} from '../../components/sales/salesUi';

const TARGET_MODES = [
  { value: 'lead', label: 'Existing Lead', icon: <PeopleAltOutlined fontSize="small" /> },
  { value: 'new_lead', label: 'New Lead', icon: <AddBusinessOutlined fontSize="small" /> },
  { value: 'customer', label: 'Existing Customer', icon: <BusinessOutlined fontSize="small" /> },
];

const ACTIVITY_TYPES = [
  { value: 'phone', label: 'Phone', icon: <PhoneInTalkOutlined fontSize="small" /> },
  { value: 'onsite_visit', label: 'Onsite Visit', icon: <DirectionsCarOutlined fontSize="small" /> },
  { value: 'email', label: 'Email', icon: <EmailOutlined fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <MoreHorizOutlined fontSize="small" /> },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_NEW_LEAD = {
  company_name: '',
  city: '',
  state: '',
  phone: '',
  contact_name: '',
  contact_phone: '',
  lead_source: '',
};

const EMPTY_ACTIVITY = {
  activity_type: 'phone',
  activity_date: today(),
  contact_name: '',
  duration_minutes: '',
  notes: '',
  outcome: '',
  next_action: '',
  next_action_date: '',
};

const toggleGroupSx = {
  flexWrap: 'wrap',
  gap: 1,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px !important',
    border: `1px solid ${SALES.line} !important`,
    color: SALES.ink,
    px: 1.75,
    gap: 0.75,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: SALES.steelSoft,
    color: SALES.steel,
    borderColor: `${SALES.steel} !important`,
    '&:hover': { bgcolor: SALES.steelSoft },
  },
};

/** Debounced remote picker used for both leads and customers. */
const AsyncPicker = ({ label, placeholder, value, onChange, fetcher, getLabel, getSubtitle }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setFetching(true);
      try {
        const results = await fetcher(input);
        if (!cancelled) setOptions(results);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setFetching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, fetcher]);

  return (
    <Autocomplete
      fullWidth
      value={value}
      options={options}
      loading={fetching}
      filterOptions={(x) => x}
      onInputChange={(_, next, reason) => {
        if (reason !== 'reset') setInput(next);
      }}
      onChange={(_, next) => onChange(next)}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      getOptionLabel={(option) => getLabel(option) || ''}
      noOptionsText={input ? 'No matches' : 'Start typing to search'}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <SalesEntityAvatar name={option.company_name} size={32} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: SALES.ink }} noWrap>
              {getLabel(option)}
            </Typography>
            <Typography variant="caption" sx={{ color: SALES.muted }} noWrap>
              {getSubtitle(option)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {fetching ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const SalesLogActivity = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillLeadId = searchParams.get('lead_id');
  const prefillCustomerId = searchParams.get('customer_id');

  const [mode, setMode] = useState(prefillCustomerId ? 'customer' : 'lead');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newLead, setNewLead] = useState(EMPTY_NEW_LEAD);
  const [activity, setActivity] = useState(EMPTY_ACTIVITY);
  const [createdLeadId, setCreatedLeadId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadPrefill = async () => {
      try {
        if (prefillLeadId) {
          const res = await salesLeadService.getById(prefillLeadId);
          setSelectedLead(res.data);
          setActivity((prev) => ({ ...prev, contact_name: res.data.contact_name || '' }));
        } else if (prefillCustomerId) {
          const res = await salesPortalService.getCustomer(prefillCustomerId);
          setSelectedCustomer(res.data);
        }
      } catch {
        // A bad deep link should not block manual selection
      }
    };
    loadPrefill();
  }, [prefillLeadId, prefillCustomerId]);

  const fetchLeads = useCallback(async (search) => {
    const res = await salesLeadService.getAll({ search: search || undefined, limit: 25 });
    return res.data || [];
  }, []);

  const fetchCustomers = useCallback(async (search) => {
    const res = await salesPortalService.getCustomers({ search: search || undefined, limit: 25 });
    return res.data || [];
  }, []);

  const setActivityField = (key) => (e) =>
    setActivity((prev) => ({ ...prev, [key]: e.target.value }));

  const setNewLeadField = (key) => (e) =>
    setNewLead((prev) => ({ ...prev, [key]: e.target.value }));

  const targetSummary = useMemo(() => {
    if (mode === 'lead' && selectedLead) {
      return {
        name: selectedLead.company_name,
        detail: [
          `Lead #${selectedLead.lead_number}`,
          selectedLead.contact_name,
          selectedLead.contact_phone || selectedLead.phone,
        ].filter(Boolean).join(' · '),
      };
    }
    if (mode === 'customer' && selectedCustomer) {
      return {
        name: selectedCustomer.company_name,
        detail: [
          `Customer #${selectedCustomer.customer_number}`,
          [selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(', '),
          selectedCustomer.phone,
        ].filter(Boolean).join(' · '),
      };
    }
    if (mode === 'new_lead' && newLead.company_name.trim()) {
      return {
        name: newLead.company_name,
        detail: [
          'New lead',
          [newLead.city, newLead.state].filter(Boolean).join(', '),
          newLead.contact_name,
        ].filter(Boolean).join(' · '),
      };
    }
    return null;
  }, [mode, selectedLead, selectedCustomer, newLead]);

  const validate = () => {
    if (mode === 'lead' && !selectedLead) return 'Select the lead you contacted.';
    if (mode === 'customer' && !selectedCustomer) return 'Select the customer you contacted.';
    if (mode === 'new_lead' && !newLead.company_name.trim()) return 'Company name is required for a new lead.';
    if (!activity.notes.trim()) return 'Notes are required so the visit history stays useful.';
    return '';
  };

  const handleSave = async ({ logAnother = false } = {}) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setMessage('');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    let leadId = mode === 'lead' ? selectedLead.id : createdLeadId;
    let customerId = mode === 'customer' ? selectedCustomer.id : null;
    let leadWasCreated = false;

    try {
      if (mode === 'new_lead' && !leadId) {
        const res = await salesLeadService.create({
          ...newLead,
          contact_phone: newLead.contact_phone || newLead.phone || '',
          status: 'contacted',
        });
        leadId = res.data.id;
        leadWasCreated = true;
        // Remember it so a retry after a failed log does not duplicate the lead
        setCreatedLeadId(leadId);
      }

      await salesActivityLogService.create({
        ...activity,
        lead_id: leadId || null,
        customer_id: customerId || null,
        duration_minutes: activity.duration_minutes
          ? parseInt(activity.duration_minutes, 10)
          : null,
        next_action_date: activity.next_action_date || null,
      });

      if (logAnother) {
        setActivity({ ...EMPTY_ACTIVITY, activity_date: activity.activity_date });
        setSelectedLead(null);
        setSelectedCustomer(null);
        setNewLead(EMPTY_NEW_LEAD);
        setCreatedLeadId(null);
        setMessage(
          leadWasCreated
            ? 'Lead created and activity logged. Ready for the next one.'
            : 'Activity logged. Ready for the next one.'
        );
        return;
      }

      if (leadWasCreated) {
        navigate(`/sales/leads/${leadId}`);
      } else if (mode === 'lead') {
        navigate(`/sales/leads/${leadId}`);
      } else {
        navigate(`/sales/customers/${customerId}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <SalesPageHeader
        title="Log Call / Visit"
        subtitle="Record a call or onsite visit in one pass — pick an existing lead or customer, or create the lead right here."
        actions={
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none', color: SALES.muted }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSave({ logAnother: true })}
              disabled={saving}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Save &amp; Log Another
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSave()}
              disabled={saving}
              sx={salesPrimaryButtonSx}
            >
              {saving ? 'Saving…' : 'Save Activity'}
            </Button>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <SalesSectionCard title="Who did you contact?" subtitle="Search what you already have, or add a brand-new lead">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, next) => {
            if (!next) return;
            setMode(next);
            setError('');
          }}
          sx={{ ...toggleGroupSx, mb: 2.5 }}
        >
          {TARGET_MODES.map((m) => (
            <ToggleButton key={m.value} value={m.value}>
              {m.icon}
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {mode === 'lead' && (
          <AsyncPicker
            label="Lead"
            placeholder="Search your leads by company, contact, or phone…"
            value={selectedLead}
            onChange={(next) => {
              setSelectedLead(next);
              if (next?.contact_name) {
                setActivity((prev) => ({ ...prev, contact_name: next.contact_name }));
              }
            }}
            fetcher={fetchLeads}
            getLabel={(o) => `#${o.lead_number} — ${o.company_name}`}
            getSubtitle={(o) =>
              [o.contact_name, o.contact_phone || o.phone, [o.city, o.state].filter(Boolean).join(', ')]
                .filter(Boolean).join(' · ') || 'No contact details yet'
            }
          />
        )}

        {mode === 'customer' && (
          <AsyncPicker
            label="Customer"
            placeholder="Search customers by company name…"
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            fetcher={fetchCustomers}
            getLabel={(o) => `#${o.customer_number} — ${o.company_name}`}
            getSubtitle={(o) =>
              [[o.city, o.state].filter(Boolean).join(', '), o.phone]
                .filter(Boolean).join(' · ') || 'No contact details on file'
            }
          />
        )}

        {mode === 'new_lead' && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Company Name" required
                value={newLead.company_name}
                onChange={setNewLeadField('company_name')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Lead Source"
                placeholder="Cold call, referral, drive-by…"
                value={newLead.lead_source}
                onChange={setNewLeadField('lead_source')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" label="Contact Name"
                value={newLead.contact_name}
                onChange={setNewLeadField('contact_name')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" label="Contact Phone"
                value={newLead.contact_phone}
                onChange={setNewLeadField('contact_phone')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" label="Company Phone"
                value={newLead.phone}
                onChange={setNewLeadField('phone')}
              />
            </Grid>
            <Grid item xs={8} md={4}>
              <TextField
                fullWidth size="small" label="City"
                value={newLead.city}
                onChange={setNewLeadField('city')}
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                fullWidth size="small" label="State"
                value={newLead.state}
                onChange={setNewLeadField('state')}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: SALES.muted }}>
                Saving creates the lead as <strong>Contacted</strong> and attaches this activity. You can fill in
                address, specs, and full notes on the lead page afterwards.
              </Typography>
            </Grid>
          </Grid>
        )}

        {targetSummary && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: SALES.steelSoft,
              border: `1px solid ${SALES.steel}22`,
            }}
          >
            <SalesEntityAvatar name={targetSummary.name} size={40} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: SALES.ink }} noWrap>
                {targetSummary.name}
              </Typography>
              <Typography variant="caption" sx={{ color: SALES.muted }} noWrap>
                {targetSummary.detail}
              </Typography>
            </Box>
          </Stack>
        )}
      </SalesSectionCard>

      <SalesSectionCard title="Call / Visit Details" subtitle="Notes are required — everything else is optional">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activity.activity_type}
          onChange={(_, next) => {
            if (!next) return;
            setActivity((prev) => ({ ...prev, activity_type: next }));
          }}
          sx={{ ...toggleGroupSx, mb: 2.5 }}
        >
          {ACTIVITY_TYPES.map((t) => (
            <ToggleButton key={t.value} value={t.value}>
              {t.icon}
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
              value={activity.activity_date}
              onChange={setActivityField('activity_date')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Contact Name"
              value={activity.contact_name}
              onChange={setActivityField('contact_name')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Duration (minutes)" type="number"
              value={activity.duration_minutes}
              onChange={setActivityField('duration_minutes')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline minRows={4} required label="Notes"
              placeholder="What was discussed, what they make, what they need…"
              value={activity.notes}
              onChange={setActivityField('notes')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="Outcome"
              placeholder="Interested, wants a quote, no need right now…"
              value={activity.outcome}
              onChange={setActivityField('outcome')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="Next Action"
              placeholder="Send quote, follow up, drop samples…"
              value={activity.next_action}
              onChange={setActivityField('next_action')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" type="date" label="Next Action Date" InputLabelProps={{ shrink: true }}
              value={activity.next_action_date}
              onChange={setActivityField('next_action_date')}
            />
          </Grid>
        </Grid>
      </SalesSectionCard>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => handleSave({ logAnother: true })}
          disabled={saving}
          sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
        >
          Save &amp; Log Another
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSave()}
          disabled={saving}
          sx={salesPrimaryButtonSx}
        >
          {saving ? 'Saving…' : 'Save Activity'}
        </Button>
      </Stack>
    </Box>
  );
};

export default SalesLogActivity;
